/**
 * Client Request ▸ **Service Allocation** (design v2.0 §12) — the dispatch
 * board.
 *
 * Request Registration answers "what did this client ask for". This answers the
 * other question dispatch has every morning: across EVERY request, what is not
 * scheduled, what has nobody to drive it, and what is ready to go. Those rows
 * are scattered one-per-request everywhere else, which is exactly why the board
 * exists.
 *
 * ONLY TWO THINGS ARE EDITABLE HERE — the date and the driver. That is the whole
 * job of allocation, and a narrow surface means the board cannot change a
 * service's category, address or answers by accident. The API endpoint is
 * equally narrow for the same reason.
 *
 * DRAFTS ARE NOT ON THE BOARD (excluded server-side). An added-but-never-saved
 * service is not work anybody has committed to, and dispatching a technician to
 * an address the client never agreed is precisely what the release gate exists
 * to prevent (§10.3).
 *
 * Every change is audited by NAME — "Assigned to: R. Avila → D. Chen" — through
 * the same rows the detail page writes, so one history covers the service
 * wherever it was edited (§11.1).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import './clientRequest.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { navigateToPage } from '../../layouts/navigateTo';
import {
  AUDIT_FORM_NAME, allocateService, allocationStatus, fetchServiceAllocation,
  fetchServiceDefinitions, fetchTechnicians, serviceReminder,
  type ServiceAllocationRow, type ServiceCategoryDef,
} from './clientRequestApi';

export default function ServiceAllocationPage() {
  const [rows,    setRows]    = useState<ServiceAllocationRow[]>([]);
  const [defs,    setDefs]    = useState<ServiceCategoryDef[]>([]);
  const [techs,   setTechs]   = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number>();
  const [savingId, setSavingId] = useState<string | null>(null);
  const [sel,     setSel]     = useState<Set<string | number>>(new Set());
  /* What the bulk bar will apply to the ticked rows. Empty means "leave this
     one alone", so a user can set a date across a dozen services without also
     having to name a driver — the two are independent decisions. */
  const [bulkDate,   setBulkDate]   = useState('');
  const [bulkDriver, setBulkDriver] = useState('');

  const notify = useNotify();
  const { confirm, alert } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [r, d, w] = await Promise.all([
        fetchServiceAllocation(), fetchServiceDefinitions(), fetchTechnicians(),
      ]);
      setRows(r); setDefs(d); setTechs(w);
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      notify.error('Could not load the service allocation board.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const catName = useCallback(
    (code?: string | null) => defs.find(d => d.code === code)?.name ?? code ?? '—', [defs]);
  const catTone = useCallback(
    (code?: string | null) => defs.find(d => d.code === code)?.tone ?? 'neutral', [defs]);


  /** Persist one row's date/driver. The row is updated from what the SERVER
   *  accepted rather than optimistically, so a rejected change never leaves the
   *  board showing an allocation that did not happen. */
  const allocate = async (
    row: ServiceAllocationRow, date: string | null, driverId: string | null,
    // `quiet` suppresses the per-row acknowledgement so a bulk apply can give
    // ONE summary instead of a stack of identical banners.
    opts?: { quiet?: boolean },
  ) => {
    setSavingId(row.id);
    try {
      await allocateService(row.id, date, driverId);
      setRows(rs => rs.map(r => r.id === row.id
        ? { ...r,
            scheduledDate: date,
            assignedToId: driverId,
            assignedToName: techs.find(t => t.id === driverId)?.label ?? null }
        : r));
      if (!opts?.quiet) notify.success('Saved successfully.');
    } catch (e) {
      notify.error(e instanceof Error ? `Could not save the change: ${e.message}`
                                      : 'Could not save the change.');
      // Re-read so the board shows what is actually stored, not the edit that failed.
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const columns: AppColumn<ServiceAllocationRow>[] = useMemo(() => [
    { field: 'serviceNo', header: 'Service', width: 150,
      text: r => r.serviceNo ?? '',
      render: r => r.serviceNo ?? <span className="na">—</span> },
    { field: 'categoryCode', header: 'Category', width: 130,
      text: r => catName(r.categoryCode),
      render: r => <span className={`cr-chip ${catTone(r.categoryCode)}`}>
        {catName(r.categoryCode)}</span> },
    { field: 'clientName', header: 'Client', width: 200,
      text: r => r.clientName ?? '',
      render: r => r.clientName
        ? <>{r.clientName}{r.projectCode ? <span className="cr-dow">· {r.projectCode}</span> : null}</>
        : <span className="na">—</span> },
    { field: 'siteAddress', header: 'Where', width: 200,
      text: r => r.siteAddress ?? '',
      render: r => r.siteAddress || <span className="na">No address</span> },

    // ── The two editable columns ─────────────────────────────────────────
    { field: 'scheduledDate', header: 'Date', width: 160,
      text: r => (r.scheduledDate ?? '').slice(0, 10),
      render: r => (
        <input type="date" className="sa-edit"
               disabled={savingId === r.id}
               value={(r.scheduledDate ?? '').slice(0, 10)}
               onClick={e => e.stopPropagation()}
               onChange={e => void allocate(r, e.target.value || null, r.assignedToId)} />
      ) },
    { field: 'assignedToName', header: 'Driver', width: 210,
      text: r => r.assignedToName ?? '',
      render: r => (
        <select className="sa-edit"
                disabled={savingId === r.id}
                value={r.assignedToId ?? ''}
                onClick={e => e.stopPropagation()}
                onChange={e => void allocate(r, r.scheduledDate, e.target.value || null)}>
          <option value="">— unassigned —</option>
          {techs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      ) },

    { field: 'id', header: 'Status', width: 150,
      text: r => allocationStatus(r).label,
      render: r => {
        const s = allocationStatus(r);
        return <span className={`cr-chip ${s.tone}`}>{s.label}</span>;
      } },
    /*
     * REMINDER, not Job (prototype v46 · aligned 2026-09-09).
     *
     * A Job number is minted by Sample Registration when it validates the eCOC
     * — i.e. AFTER the visit — so on a PRE-dispatch board it was blank for
     * precisely the rows that still needed action. v46 shows Job on Service
     * Tracking, beside Arrived and Completed, where by then it exists.
     *
     * What earns the space here is the one thing to do about the row next.
     * Deliberately distinct from Status beside it: Status says how far along
     * the row is, this says what to do about it.
     */
    { field: 'reminder', header: 'Reminder', width: 110, align: 'center',
      sortable: false,
      // The words, not the icon: an export or a column filter has to be
      // readable, and "Ready to dispatch." is what the user actually saw.
      text: r => serviceReminder(r).tip,
      render: r => {
        const t = serviceReminder(r);
        return (
          <span className={`cr-rtip ${t.tone}`} title={t.tip} aria-label={t.tip} role="img">
            <i className={`ti ${t.icon}`} aria-hidden="true" />
          </span>
        );
      } },
  ], [catName, catTone, techs, savingId]);   // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Apply the bulk bar's date and/or driver to every ticked row.
   *
   * An empty field means "leave this one alone" rather than "clear it": a user
   * setting a date across a dozen services must not silently unassign all their
   * drivers because they did not also pick one. A driver is cleared one row at a
   * time, through that row's own "— unassigned —" option.
   *
   * Rows are written one at a time through the same audited endpoint a single
   * edit uses, so a bulk move produces the same history as twelve individual
   * ones — the audit trail should not be able to tell how the change was made.
   */
  const applyBulk = async () => {
    const picked = rows.filter(r => sel.has(r.id));
    /* Same reasoning as "Nothing to apply" below: a button that silently does
       nothing reads as broken. Nothing ticked is the OTHER half of the same
       mistake, and it was the half that said nothing. */
    if (!picked.length) {
      await alert({
        title: 'Nothing selected',
        message: 'Tick the services you want to change in the list first, then Apply '
               + 'sets the date and driver on all of them at once.',
        tone: 'info',
      });
      return;
    }

    /* Apply stays clickable (owner request 2026-09-09) rather than sitting
       greyed until a field is filled. A button that does nothing when pressed
       reads as broken, so pressing it with nothing chosen SAYS what is missing
       instead of silently returning. */
    if (!bulkDate && !bulkDriver) {
      await alert({
        title: 'Nothing to apply',
        message: 'Pick a date, a driver, or both — then Apply sets them on the '
               + 'selected services.',
        tone: 'info',
      });
      return;
    }

    const what = [
      bulkDate   ? `dated ${bulkDate}` : null,
      bulkDriver ? `assigned to ${techs.find(t => t.id === bulkDriver)?.label ?? 'a driver'}` : null,
    ].filter(Boolean).join(' and ');

    const ok = await confirm({
      title: 'Apply to the selected services?',
      message: `${picked.length} service${picked.length > 1 ? 's' : ''} will be ${what}. `
             + 'Every change is audited.',
    });
    if (!ok) return;

    /* One summary acknowledgement, not one per row: twelve stacked "Saved
       successfully." banners bury the one thing worth reading, which is how
       many services actually moved. */
    let changed = 0;
    for (const r of picked) {
      const date   = bulkDate   || r.scheduledDate;
      const driver = bulkDriver || r.assignedToId;
      if ((date ?? null) === (r.scheduledDate ?? null)
       && (driver ?? null) === (r.assignedToId ?? null)) continue;
      await allocate(r, date, driver, { quiet: true });
      changed++;
    }
    notify.success(changed
      ? `${changed} change${changed === 1 ? '' : 's'} applied and audited.`
      : 'Nothing to change — those services already had that date and driver.');
    setSel(new Set()); setBulkDate(''); setBulkDriver('');
  };

  /* The bulk "Unassign" action was removed on owner request (2026-09-09).
     Nothing became unclearable: a row's own Date input and its "— unassigned —"
     driver option both send null through the SAME audited endpoint. What is
     gone is clearing an entire SELECTION in one step. */

  // Keyboard shortcut (CLAUDE.md standing rule 14, §18.6, ADR-0013): the bulk
  // Apply button lives in the selection bar (shown only when rows are ticked),
  // so gate the def in/out to match, and mirror its `disabled` (a write in
  // flight).  Alt+A = Apply.
  useHotkeys(
    sel.size > 0
      ? [{ combo: 'alt+a', label: 'Apply', group: 'Client Request',
           run: () => void applyBulk(), disabled: !!savingId },
         { combo: 'alt+shift+c', label: 'Clear', group: 'Client Request',
           run: () => setSel(new Set()) }]
      : [],
  );

  return (
    <div className="bl-page">
      <div className="bl-top">
        {/* The SHARED list-page header block, identical to Request
            Registration's: `.dp-page-hdr` wrapping breadcrumb + title + subtitle.
            The wrapper is what carries the spacing — without it the title sat
            hard against the top of the page and the toolbar hard against the
            subtitle. */}
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Client Request</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Service Allocation</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="svcaloc" loadMs={loadMs} loading={loading}
                       auditFormName={AUDIT_FORM_NAME} />
          </div>
          <div className="dp-subtitle">
            Move services between dates and drivers. Every change is audited.
          </div>
        </div>

        <AppToolbar
          left={
            /* Opens Service Scheduling — the dedicated calendar over these
               same services, with Day / Week / Month / Timeline. It is a
               screen rather than a mode here because the calendar has its own
               navigation entry and its own four views; keeping a second,
               smaller copy inline would have been two drag-and-drop surfaces
               writing the same audit rows. */
            <button className="ca-btn" onClick={() => navigateToPage('svcsched')}>
              <span className="tb-i"><i className="ti ti-calendar-month" /></span>
              Calendar view
            </button>
          }
          onRefresh={() => void load()}
          refreshing={loading}
          refreshDisabled={loading}
          exportData={{ ...gridExportMatrix(rows, columns), filename: 'service-allocation' }}
        />

        {/* Ticked rows show the bulk bar. The orange counts/hint strip was
            removed app-wide (user request 2026-09-19). */}
        {sel.size > 0 && (
          <div className="sa-bulk">
            <span className="sa-bulk-n">{sel.size} selected</span>
            <input type="date" className="sa-bulk-f" value={bulkDate}
                   aria-label="Date for the selected services"
                   onChange={e => setBulkDate(e.target.value)} />
            <select className="sa-bulk-f" value={bulkDriver}
                    aria-label="Driver for the selected services"
                    onChange={e => setBulkDriver(e.target.value)}>
              <option value="">Assign driver…</option>
              {techs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {/* Enabled whenever rows are ticked (owner request 2026-09-09). The
                only guard left is `savingId` — a write is in flight, and letting
                a second one start would race it. */}
            <button className="ca-btn ca-btn-solid"
                    disabled={!!savingId}
                    title={`Apply (${formatCombo('alt+a')})`}
                    onClick={() => void applyBulk()}>
              <span className="tb-i"><i className="ti ti-check" /></span> Apply
            </button>
            <span className="sc-spacer" />
            <button className="ca-btn cr-discard" onClick={() => setSel(new Set())}
                    title={`Clear (${formatCombo('alt+shift+c')})`}>Clear</button>
          </div>
        )}
      </div>

      <div className="bl-grid-area" style={{ marginTop: 10 }}>
        <div className="bl-card bl-card-flex">
          <AppDataGrid<ServiceAllocationRow>
            rows={rows}
            columns={columns}
            getRowId={r => r.id}
            loading={loading}
            loadMs={loadMs}
            defaultPageSize={20}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            emptyText="No saved services yet — register a request and add a service."
          />
        </div>
      </div>
    </div>
  );
}
